import classNames from "classnames"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import Callout from "@components/elements/V2/Callout"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Section from "@components/elements/V2/Section"
import Typography from "@components/elements/V2/Typography"
import RichTextElement from "@components/StoryBlok/RichTextElement"
import HeroLayout from "@components/V2/Heroes/HeroLayout"
import { Color } from "@constants/V2/color"
import { useElementObserver } from "@hooks/V2/useElementObserver"
import { usePageLayoutState } from "@utils/layout"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React, { useEffect, useRef } from "react"

type Props = {
  backgroundColor: Color
  cardColor?: Color
  backgroundImageUrl: string
  backgroundImageAlt: string
  mobileImageUrl?: string
  mobileImageAlt?: string
  cloudLogoStripUrl?: string
  cloudLogoStripAlt?: string
  cloudLogoStripMobileUrl?: string
  cloudLogoStripMobileAlt?: string
  title: string
  description: string
  richDescription: Storyblok.RichTextBlock
  primaryButtonText: string
  primaryButtonLink: string | YocoCom.YocoLink | undefined
  hideMobileStickyButton: boolean
  secondaryButtonText: string
  secondaryButtonLink: string | YocoCom.YocoLink | undefined
  callout: Storyblok.Callout
}

const ContentBorderedHero = ({
  backgroundColor,
  cardColor,
  backgroundImageAlt,
  backgroundImageUrl,
  mobileImageUrl,
  mobileImageAlt,
  cloudLogoStripUrl,
  cloudLogoStripAlt,
  cloudLogoStripMobileUrl,
  cloudLogoStripMobileAlt,
  title,
  description,
  richDescription,
  primaryButtonText,
  primaryButtonLink,
  hideMobileStickyButton,
  secondaryButtonText,
  secondaryButtonLink,
  callout,
}: Props) => {
  const ctaContainerRef = useRef(null)

  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(ctaContainerRef)

  startObserving()

  const { setHeroCTAVisible, hasAnchorNavigation } = usePageLayoutState()

  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)

    return () => {
      setHeroCTAVisible(false)
    }
  }, [isCtaVisible, setHeroCTAVisible])

  const accentColor = getAccentColor(backgroundColor)

  return (
    <HeroLayout
      primaryButtonText={primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonLink}
      primaryButtonColor={Color.Blue}
      hideMobileButton={hideMobileStickyButton}
    >
      <Section
        className={classNames(
          "-mb-lg flex items-center justify-center pb-32 sm:pb-40",
          hasAnchorNavigation ? "pt-12 sm:pt-navHeight-xs" : "pt-navHeight-xs",
          getBackgroundColorClass(backgroundColor)
        )}
      >
        <div
          ref={ctaContainerRef}
          className={classNames(
            "relative mt-16 flex w-full flex-row justify-between rounded-20 md:mt-20",
            getBackgroundColorClass(backgroundColor)
          )}
        >
          {backgroundImageUrl && (
            <div className="absolute left-0 top-0 hidden h-full w-full sm:block">
              <GatsbyStoryblokImage
                image={backgroundImageUrl}
                alt={backgroundImageAlt || "Background image"}
                loadType="eager"
                fetchPriority="high"
                placeholderColor={backgroundColor}
                quality={80}
                aspectRatio="fullWidth"
                rounded
                className="h-full w-auto"
              />
            </div>
          )}

          {/* desktop */}
          <div className="z-10 hidden w-full flex-col py-24 sm:flex sm:max-w-[515px] sm:px-[70px] md:py-60 lg:w-1/2 lg:max-w-full lg:py-88 xl:py-[120px]">
            {callout && (
              <Callout
                color={Color.White}
                text={callout.text}
                highlightedWord={callout.highlightedWord}
                highlightColor={callout.highlightColor}
                hasBorder={callout.hasBorder}
                hasIcon={callout.hasIcon}
                classname="mb-8"
              />
            )}

            <Typography
              element="h1"
              text={title}
              font="grotesk"
              weight="medium"
              size="h4"
              color={cardColor || Color.White}
              className="mb-20 text-balance"
            />

            <Typography
              text={description}
              font="grotesk"
              weight="book"
              size="body-lg"
              color={cardColor || Color.White}
              className="mb-40"
            />

            <RichTextElement
              content={richDescription}
              textColor={cardColor || Color.White}
              className="text-responsive-body-lg mb-40 text-balance font-grotesk font-normal"
            />

            <div className="flex flex-row items-center gap-32">
              <PillButton
                size="medium"
                style="solid"
                text={primaryButtonText}
                color={Color.Blue}
                linkUrl={primaryButtonLink}
                hideIcon
              />
              <TextButton
                style="text"
                text={secondaryButtonText}
                linkUrl={secondaryButtonLink}
                color={Color.White}
                className="[&_p]:!pt-0 [&_p]:md:mt-[5px]"
              />
            </div>

            {cloudLogoStripUrl && (
              <div className="mt-40 flex flex-col items-start gap-2 pb-0 sm:pb-20">
                <Typography
                  text="Cloud integration partners"
                  font="grotesk"
                  weight="book"
                  size="body-sm"
                  color={cardColor || Color.White}
                  className="opacity-50"
                />
                <GatsbyStoryblokImage
                  loadType="eager"
                  image={cloudLogoStripUrl}
                  alt={cloudLogoStripAlt || ""}
                  className="h-[37] w-auto"
                />
              </div>
            )}
          </div>

          {/* mobile */}
          <div className="z-10 flex flex-col sm:hidden">
            {callout && (
              <Callout
                color={accentColor}
                text={callout.text}
                highlightedWord={callout.highlightedWord}
                highlightColor={callout.highlightColor}
                hasBorder={callout.hasBorder}
                hasIcon={callout.hasIcon}
                classname="mb-8"
              />
            )}

            <Typography
              element="h1"
              text={title}
              font="grotesk"
              weight="medium"
              size="h4"
              color={accentColor}
              className="mb-20"
            />

            <Typography
              text={description}
              font="grotesk"
              weight="book"
              size="body-lg"
              color={accentColor}
              className="mb-40"
            />

            <RichTextElement
              content={richDescription}
              textColor={accentColor}
              className="text-responsive-body-lg mb-40 font-grotesk font-normal"
            />

            <div className="flex flex-col items-center gap-16">
              <PillButton
                size="medium"
                style="solid"
                text={primaryButtonText}
                color={Color.Blue}
                linkUrl={primaryButtonLink}
                hideIcon
              />
              <TextButton
                style="text"
                text={secondaryButtonText}
                linkUrl={secondaryButtonLink}
                color={accentColor}
                className="[&_p]:!pt-0"
              />
            </div>

            {(cloudLogoStripMobileUrl || cloudLogoStripUrl) && (
              <div className="mt-72 flex flex-col items-center gap-16">
                <Typography
                  text="Cloud integration partners"
                  font="grotesk"
                  weight="book"
                  size="body-sm"
                  color={accentColor}
                  className="opacity-50"
                />
                <GatsbyStoryblokImage
                  loadType="eager"
                  image={cloudLogoStripMobileUrl || cloudLogoStripUrl || ""}
                  alt={cloudLogoStripMobileAlt || cloudLogoStripAlt || ""}
                  className="h-20 w-auto"
                />
              </div>
            )}

            {mobileImageUrl && (
              <div className="mx-auto mt-20">
                <GatsbyStoryblokImage
                  image={mobileImageUrl}
                  alt={mobileImageAlt || "Mobile image"}
                  loadType="eager"
                  fetchPriority="high"
                  placeholderColor={backgroundColor}
                  quality={80}
                  aspectRatio="1:1"
                  className="rounded-12"
                />
              </div>
            )}
          </div>
        </div>
      </Section>
    </HeroLayout>
  )
}

export default ContentBorderedHero
